import {
  Box,
  Card,
  CardContent,
  Chip,
  FormControl,
  Stack,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Avatar,
  useMediaQuery,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import BlockIcon from '@mui/icons-material/Block';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import RestoreIcon from '@mui/icons-material/Restore';
import { useEffect, useState } from 'react';
import { api } from '../lib/apiClient';
import { useAuth } from '../state/AuthContext';
import { useToast } from '../state/ToastContext';
import { useTranslation } from 'react-i18next';
import { parseErrorMessage } from '../utils/errorUtils';
import ConfirmationDialog from '../components/ConfirmationDialog';
import CustomButton from '../components/CustomButton';
import type { Tenant } from '../types';

interface Session {
  id: string;
  userId: string;
  clientId: string | null;
  clientName?: string;
  lastActivity: number;
  createdAt: number;
  expiresAt: number;
  user?: {
    username: string;
    display_name: string;
    role: string;
  };
}

interface RevokedSession {
  id: string;
  userId: string;
  clientId: string | null;
  clientName?: string;
  createdAt: number;
  lastActivity: number;
  revokedAt: number;
  revokedUntil: number | null;
  isTemporaryRevocation: boolean;
  remainingRevocationSeconds: number | null;
  revokedReason: string | null;
  revokedBy: {
    displayName: string;
    username: string;
  } | null;
  user: {
    id: string;
    username: string;
    display_name: string;
    email: string;
    role: string;
    disabled: number;
    isDisabled: boolean;
  } | null;
}



export default function ActiveSessionsPage() {
  const theme = useTheme();
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const { t } = useTranslation();

  const REASON_LABELS: Record<
    string,
    { label: string; color: 'error' | 'warning' | 'info' | 'default' }
  > = {
    MANUAL_REVOCATION: { label: t('activeSessions.reasonLabels.manualRevocation'), color: 'warning' },
    USER_DISABLED: { label: t('activeSessions.reasonLabels.userDisabled'), color: 'error' },
    USER_DISABLED_BY_ADMIN: { label: t('activeSessions.reasonLabels.userDisabledByAdmin'), color: 'error' },
    LOGOUT: { label: t('activeSessions.reasonLabels.logout'), color: 'info' },
    USER_ACTION: { label: t('activeSessions.reasonLabels.userAction'), color: 'info' },
    SESSION_EXPIRED: { label: t('activeSessions.reasonLabels.sessionExpired'), color: 'default' },
    PASSWORD_CHANGED: { label: t('activeSessions.reasonLabels.passwordChanged'), color: 'warning' },
  };

  const getReasonChip = (reason: string | null) => {
    const mapped = reason ? REASON_LABELS[reason] : null;
    return (
      <Chip
        label={mapped?.label || reason || t('activeSessions.unknown')}
        color={mapped?.color || 'default'}
        size="small"
        variant="outlined"
        sx={{ fontWeight: 500 }}
      />
    );
  };

  const [tabIndex, setTabIndex] = useState(0);

  // Sessões ativas
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loadingActive, setLoadingActive] = useState(false);

  // Sessões revogadas
  const [revokedSessions, setRevokedSessions] = useState<RevokedSession[]>([]);
  const [loadingRevoked, setLoadingRevoked] = useState(false);

  // Filtro por tenant (apenas master)
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [loadingTenants, setLoadingTenants] = useState(false);

  // Diálogo de revogação
  const [revokeTarget, setRevokeTarget] = useState<string | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [sessionRevocationDuration, setSessionRevocationDuration] = useState<number | null>(30);

  // Diálogo de reativação
  const [reactivateTarget, setReactivateTarget] = useState<RevokedSession | null>(null);

  const isMaster = user?.isMaster ?? false;
  const isDealer = user?.role === 'dealer_admin';

  const fetchTenants = async () => {
    if (!isMaster) return;
    try {
      setLoadingTenants(true);
      const data = await api.listTenants(null);
      setTenants(data);
    } catch (error) {
      showError(parseErrorMessage(error));
    } finally {
      setLoadingTenants(false);
    }
  };

  const fetchActiveSessions = async () => {
    try {
      setLoadingActive(true);
      const tenantFilter = isMaster && selectedTenantId ? selectedTenantId : undefined;
      const res = await api.listActiveSessions(null, tenantFilter);
      setSessions(res);
    } catch (error) {
      showError(parseErrorMessage(error));
    } finally {
      setLoadingActive(false);
    }
  };

  const fetchRevokedSessions = async () => {
    try {
      setLoadingRevoked(true);
      const tenantFilter = isMaster && selectedTenantId ? selectedTenantId : undefined;
      const res = await api.listRevokedSessions(null, tenantFilter);
      setRevokedSessions(res);
    } catch (error) {
      showError(parseErrorMessage(error));
    } finally {
      setLoadingRevoked(false);
    }
  };

  useEffect(() => {
    fetchTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const doFetch = async () => {
      try {
        setLoadingActive(true);
        setLoadingRevoked(true);
        const tenantFilter = isMaster && selectedTenantId ? selectedTenantId : undefined;
        const [active, revoked] = await Promise.all([
          api.listActiveSessions(null, tenantFilter),
          api.listRevokedSessions(null, tenantFilter),
        ]);
        setSessions(active);
        setRevokedSessions(revoked);
      } catch (error) {
        showError(parseErrorMessage(error));
      } finally {
        setLoadingActive(false);
        setLoadingRevoked(false);
      }
    };

    doFetch();
    const interval = setInterval(doFetch, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenantId, isMaster]);

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await api.revokeActiveSession(null, revokeTarget, {
        reason: revokeReason || undefined,
        durationMinutes: sessionRevocationDuration,
      });
      showSuccess(t('activeSessions.toast.revoked'));
      fetchActiveSessions();
      fetchRevokedSessions();
    } catch (error) {
      showError(parseErrorMessage(error));
    } finally {
      setRevokeTarget(null);
      setRevokeReason('');
      setSessionRevocationDuration(30);
    }
  };

  const handleReactivate = async () => {
    if (!reactivateTarget) return;
    try {
      await api.reactivateSessionUser(null, reactivateTarget.userId);
      showSuccess(t('activeSessions.toast.reactivated', { name: reactivateTarget.user?.display_name }));
      fetchRevokedSessions();
    } catch (error) {
      showError(parseErrorMessage(error));
    } finally {
      setReactivateTarget(null);
    }
  };

  const handleUnrevoke = async (sessionId: string) => {
    try {
      await api.unrevokeSession(null, sessionId);
      showSuccess(t('activeSessions.toast.restored'));
      fetchRevokedSessions();
      fetchActiveSessions();
    } catch (error) {
      showError(parseErrorMessage(error));
    }
  };

  const formatTime = (ts: number) => new Date(ts * 1000).toLocaleString();

  const getRelativeTime = (ts: number) => {
    const diff = Math.floor(Date.now() / 1000) - ts;
    if (diff < 60) return t('activeSessions.timeAgo.seconds', { count: diff });
    if (diff < 3600) return t('activeSessions.timeAgo.minutes', { count: Math.floor(diff / 60) });
    if (diff < 86400) return t('activeSessions.timeAgo.hours', { count: Math.floor(diff / 3600) });
    return t('activeSessions.timeAgo.days', { count: Math.floor(diff / 86400) });
  };

  const isLoading = tabIndex === 0 ? loadingActive : loadingRevoked;

  return (
    <Box>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Stack direction="row" alignItems="center" gap={2}>
          {isDealer && user?.dealerId && (
            <Chip
              label={t('activeSessions.dealerPrefix', { name: user.displayName })}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          {!isMaster && !isDealer && user?.tenantId && (
            <Chip
              label={t('activeSessions.tenantPrefix', { name: user.displayName })}
              size="small"
              color="secondary"
              variant="outlined"
            />
          )}
        </Stack>
        <Stack direction="row" gap={1} alignItems="center">
          {isMaster && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="tenant-filter-label">{t('activeSessions.filters.tenantFilter')}</InputLabel>
              <Select
                labelId="tenant-filter-label"
                label={t('activeSessions.filters.tenantFilter')}
                value={selectedTenantId}
                onChange={(e) => setSelectedTenantId(e.target.value)}
                disabled={loadingTenants}
              >
                <MenuItem value="">{t('activeSessions.filters.allTenants')}</MenuItem>
                {tenants.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Tooltip title={t('common.refresh')}>
            <span>
              <CustomButton
                variant="icon"
                icon={<RefreshIcon />}
                iconPosition="only"
                customSize={{ width: 36, height: 36 }}
                onClick={() => {
                  fetchActiveSessions();
                  fetchRevokedSessions();
                }}
                disabled={isLoading}
              />
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Tabs */}
      <Card>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 48,
            },
          }}
        >
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                {t('activeSessions.tabs.active')}
                <Chip
                  label={sessions.length}
                  size="small"
                  color="success"
                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                />
              </Box>
            }
          />
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={1}>
                {t('activeSessions.tabs.revoked')}
                <Chip
                  label={revokedSessions.length}
                  size="small"
                  color="error"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                />
              </Box>
            }
          />
        </Tabs>

        <CardContent sx={{ p: 0 }}>
          {/* ======== ABA: SESSÕES ATIVAS ======== */}
          {tabIndex === 0 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('activeSessions.table.user')}</TableCell>
                    <TableCell>{t('activeSessions.table.type')}</TableCell>
                    <TableCell>{t('activeSessions.table.start')}</TableCell>
                    <TableCell>{t('activeSessions.table.lastActivity')}</TableCell>
                    <TableCell>{t('activeSessions.table.status')}</TableCell>
                    <TableCell align="right">{t('activeSessions.table.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions.length === 0 && !loadingActive && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          {t('activeSessions.empty.active')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {sessions.map((session) => (
                    <TableRow key={session.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2">
                            {session.user?.display_name || t('activeSessions.unknown')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {session.user?.username} • {session.user?.role}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {session.clientId ? (
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {session.clientName || t('activeSessions.externalApp')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {session.clientId}
                            </Typography>
                          </Box>
                        ) : (
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {t('activeSessions.adminPanel')}
                            </Typography>
                            <Chip
                              label={t('activeSessions.status.internal')}
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ mt: 0.5, height: 20, fontSize: '0.65rem' }}
                            />
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{formatTime(session.createdAt)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {getRelativeTime(session.lastActivity)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('activeSessions.status.expiresAt')} {formatTime(session.expiresAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={t('activeSessions.status.active')} size="small" color="success" />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('common.revoke')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setRevokeTarget(session.id)}
                            aria-label={t('activeSessions.a11y.revokeSession', { name: session.user?.display_name || t('activeSessions.unknown') })}
                          >
                            <BlockIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ======== ABA: SESSÕES REVOGADAS ======== */}
          {tabIndex === 1 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('activeSessions.table.user')}</TableCell>
                    <TableCell>{t('activeSessions.table.reason')}</TableCell>
                    <TableCell>{t('activeSessions.table.revokedBy')}</TableCell>
                    <TableCell>{t('activeSessions.table.revokedAt')}</TableCell>
                    <TableCell>{t('activeSessions.table.userStatus')}</TableCell>
                    <TableCell align="right">{t('activeSessions.table.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {revokedSessions.length === 0 && !loadingRevoked && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          {t('activeSessions.empty.revoked')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {revokedSessions.map((session) => (
                    <TableRow key={session.id} hover>
                      {/* Usuário */}
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Avatar
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: session.user?.isDisabled
                                ? alpha(theme.palette.error.main, 0.15)
                                : alpha(theme.palette.primary.main, 0.15),
                              color: session.user?.isDisabled
                                ? theme.palette.error.main
                                : theme.palette.primary.main,
                              fontSize: '0.85rem',
                              fontWeight: 700,
                            }}
                          >
                            {session.user?.isDisabled ? (
                              <PersonOffIcon sx={{ fontSize: 18 }} />
                            ) : (
                              (session.user?.display_name || '?').charAt(0).toUpperCase()
                            )}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {session.user?.display_name || t('activeSessions.unknown')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {session.user?.username} • {session.user?.role}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Motivo */}
                      <TableCell>{getReasonChip(session.revokedReason)}</TableCell>

                      {/* Revogado por */}
                      <TableCell>
                        {session.revokedBy ? (
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {session.revokedBy.displayName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              @{session.revokedBy.username}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            {t('activeSessions.status.system')}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Data */}
                      <TableCell>
                        {session.isTemporaryRevocation && session.revokedUntil ? (
                          <Box sx={{ mb: 0.5 }}>
                            <Typography variant="caption" color="warning.main" fontWeight={700}>
                              {t('activeSessions.status.temporaryBlock')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t('activeSessions.status.until')} {formatTime(session.revokedUntil)}
                            </Typography>
                          </Box>
                        ) : null}

                        {session.revokedAt ? (
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {getRelativeTime(session.revokedAt)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatTime(session.revokedAt)}
                            </Typography>
                          </Box>
                        ) : (
                          '—'
                        )}
                      </TableCell>

                      {/* Status do Usuário */}
                      <TableCell>
                        {session.user?.isDisabled ? (
                          <Chip
                            label={t('activeSessions.status.disabled')}
                            size="small"
                            color="error"
                            icon={<PersonOffIcon />}
                          />
                        ) : (
                          <Chip label={t('activeSessions.status.active')} size="small" color="success" />
                        )}
                      </TableCell>

                      {/* Ações */}
                      <TableCell align="right">
                        {session.user?.isDisabled && (
                          <Tooltip title={t('common.reactivate')}>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => setReactivateTarget(session)}
                              aria-label={t('activeSessions.a11y.reactivateUser', { name: session.user?.display_name || t('activeSessions.unknown') })}
                            >
                              <RestoreIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        <Tooltip title={t('common.restore')}>
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleUnrevoke(session.id)}
                            aria-label={t('activeSessions.a11y.restoreSession', { name: session.user?.display_name || t('activeSessions.unknown') })}
                          >
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <ConfirmationDialog
        open={!!revokeTarget}
        onClose={() => {
          setRevokeTarget(null);
          setRevokeReason('');
          setSessionRevocationDuration(30);
        }}
        onConfirm={handleRevoke}
        title={t('activeSessions.dialog.revokeTitle')}
        content={t('activeSessions.dialog.revokeContent')}
        severity="error"
        confirmText={t('activeSessions.dialog.revokeConfirm')}
        cancelText={t('activeSessions.dialog.revokeCancel')}
        customContent={
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              type="number"
              label={t('activeSessions.dialog.durationLabel')}
              placeholder={t('activeSessions.dialog.durationPlaceholder')}
              value={sessionRevocationDuration ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setSessionRevocationDuration(null);
                  return;
                }

                const parsed = Number.parseInt(value, 10);
                if (Number.isNaN(parsed) || parsed <= 0) {
                  setSessionRevocationDuration(null);
                  return;
                }

                setSessionRevocationDuration(parsed);
              }}
              helperText={t('activeSessions.dialog.durationHelper')}
              size="small"
            />
            <TextField
              fullWidth
              label={t('activeSessions.dialog.reasonLabel')}
              placeholder={t('activeSessions.dialog.reasonPlaceholder')}
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
              multiline
              minRows={2}
              maxRows={4}
              size="small"
            />
          </Stack>
        }
      />

      <ConfirmationDialog
        open={!!reactivateTarget}
        title={t('activeSessions.dialog.reactivateTitle')}
        content={t('activeSessions.dialog.reactivateContent', { name: reactivateTarget?.user?.display_name || '' })}
        confirmText={t('activeSessions.dialog.reactivateConfirm')}
        onConfirm={handleReactivate}
        onClose={() => setReactivateTarget(null)}
        severity="success"
      />
    </Box>
  );
}
